// ── Agenda (Plano 04) ────────────────────────────────────────────────────
export type AgendaType = 'edital' | 'certidao' | 'contrato' | 'sessao';

export interface AgendaItem {
  id: string;
  date: string;           // ISO "YYYY-MM-DDTHH:mm:ssZ"
  hour?: number;          // 0-23 opcional
  type: AgendaType;
  title: string;
  subtitle?: string;
  value?: string | number;
  relatedId?: string;
  priority?: 'low' | 'medium' | 'high';
  done?: boolean;
}

export enum TenderStatus {
  OPEN = 'Aberta',
  CLOSED = 'Fechada',
  ANALYSIS = 'Em Análise',
  WON = 'Ganha',
  LOST = 'Perdida',
  // Novos status solicitados
  PARTICIPATE = 'Participar',
  HIGH_PRIORITY = 'Alta Prioridade',
  EXTREME_PRIORITY = 'Altíssima Prioridade',
  NO_GO = 'Não Participar',
  // Novos status de mercado
  ELECTRONIC_QUOTE = 'Cotação Eletrônica',
  DIRECT_WAIVER = 'Dispensa Direta',
  RANDOM = 'Randômica',
}

// Interface para itens de licitação externa
export interface ExternalTenderItem {
  descricao: string;
  quantidade: number;
  unidade: string;
  valorUnitario: number;
  valorTotal: number;
  // Novo mapeamento solicitado
  item?: number; // número do item dentro do edital
  lote?: string | number; // lote quando houver
  diferenciado?: boolean; // flag Exclusivo ME/EPP (diferenciado)
  grupo?: string; // grupo/categoria do item
}

// Interface para licitações externas (MongoDB, APIs públicas, etc.)
export interface ExternalTender {
  id: string;
  edital: string;
  estado: string;
  cidade: string;
  orgao: string;
  objeto: string;
  modalidade: string;
  recebimentoPropostas: string;
  encerramento: string;
  incluido: string;
  atualizado?: string;
  itens?: ExternalTenderItem[];
  valorTotal?: number;
  urlEdital?: string;
  fonte?: string;
  observacoes?: string;
  relevanceScore?: number | null;
  relevanceLevel?: 'MUITO_ALTA' | 'ALTA' | 'MEDIA' | 'BAIXA' | null;
  matchDetails?: {
    accepted?: boolean;
    category?: string;
    profile?: string;
    matchedTerms?: string[];
    commercialTerms?: string[];
    supportTerms?: string[];
    softExclusions?: string[];
    businessModels?: string[];
    reasons?: string[];
  } | null;
  // Campos adicionais mapeados da API Effecti
  portal?: string;
  uasg?: number;
  perfilNome?: string;
  portalUrl?: string;
  attachmentUrl?: string;
  attachments?: DocumentAttachment[];
  terms?: Array<{ id: string; term: string; label?: string | null }>;
  favoriteTenderId?: string | null;
  favoriteWorkflowStage?: TenderWorkflowStage | null;
  favoritePriority?: TenderPriority | null;
  favoriteParticipationDecision?: ParticipationDecision | null;
  favoriteTrackResult?: boolean;
  favoriteArchiveReason?: 'NO_PARTICIPATION' | 'DEADLINE' | null;
  ativo?: boolean;
  readAt?: string | null;
  readByName?: string | null;
  discardedAt?: string | null;
  discardedByName?: string | null;
  discardReason?: 'USER' | 'AUTO_RETENTION' | 'NO_PARTICIPATION' | string | null;
  restoredAt?: string | null;
}

export type TenderWorkflowStage = 'PIPELINE' | 'FAVORITED' | 'ONGOING' | 'PARTICIPATION' | 'AUCTION' | 'CLOSED';
export type TenderPriority = 'NONE' | 'PRIORITY' | 'VERY_HIGH' | 'ULTRA' | 'NO_GO';
export type ParticipationDecision = 'PENDING' | 'PARTICIPATE' | 'DO_NOT_PARTICIPATE';

export interface Tender {
  id: string;
  title: string;
  organ: string;
  portal?: string; // Novo campo Portal
  state: string; // Novo campo UF
  publicationDate?: string; // Novo campo Data de Publicação
  deadline: string;
  status: TenderStatus;
  value: number;
  description: string;
  fileUrl?: string; // URL/caminho do PDF do edital
  // Campos para Agenda / Próxima Ação
  nextAction?: string; // Ex: "Enviar Proposta", "Disputa de Lances"
  nextActionDate?: string; // Data da ação (YYYY-MM-DD)
  nextActionTime?: string; // Hora da ação (HH:mm)

  // Novos campos de Checklist
  isRegistered?: boolean; // Licitação Cadastrada no Portal
  areDocsReady?: boolean; // Documentos Prontos/Separados
  workflowStage?: TenderWorkflowStage;
  priority?: TenderPriority;
  participationDecision?: ParticipationDecision;
  documentsSeparated?: boolean;
  documentsValidated?: boolean;
  brandsValidated?: boolean;
  modelsValidated?: boolean;
  valuesValidated?: boolean;
  sourceExternalTenderId?: string | null;
  opportunityCategory?: string | null;
  sourceObject?: string | null;
  auctionStatus?: 'DRAFT' | 'RUNNING' | 'PAUSED' | 'FINISHED' | null;
  auctionPhase?: string | null;
  favoritedAt?: string | null;
  ongoingAt?: string | null;
  participationAt?: string | null;
  trackResult?: boolean;
  resultTrackingAt?: string | null;
}

export type TenderResultStatus = 'WON' | 'LOST' | 'DISQUALIFIED' | 'JUDGING';
export type TenderResultProofStatus = 'PENDING' | 'ATTACHED' | 'WAIVED';
export type TenderResultProofMethod = 'MANUAL' | 'LINK' | 'UPLOAD';

export interface TenderResultCompetitor {
  id?: string;
  placement: number | string;
  companyName: string;
  bidValue?: number | string | null;
  notes?: string | null;
}

export interface TenderResult {
  id: string;
  tenderId: string;
  tender?: Tender;
  resultStatus: TenderResultStatus;
  resultDate?: string | null;
  proofRequired: boolean;
  proofStatus: TenderResultProofStatus;
  proofName?: string | null;
  proofUrl?: string | null;
  proofType?: string | null;
  proofMethod?: TenderResultProofMethod;
  proofNotes?: string | null;
  ourPlacement?: number | string | null;
  competitors?: TenderResultCompetitor[];
  disqualificationReason?: string | null;
  lossReason?: string | null;
  ourBidValue?: number | string | null;
  winnerBidValue?: number | string | null;
  winnerName?: string | null;
  category?: string | null;
  segment?: string | null;
  modality?: string | null;
  executiveNotes?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface AnalyzedFile {
  name: string;
  type: string;
  size: number;
  base64: string | null;
  url?: string;
}

export interface RenewalRecord {
  id?: string;
  renewalDate: string;
  newEndDate: string;
  adjustmentPercentage?: number;
  previousMonthlyValue?: number;
  newMonthlyValue?: number;
  file?: AnalyzedFile | null; // Arquivo do Aditivo
}

// --- NOVO TIPO DE CONTRATO ---
export type ContractType = 'SUPPLY' | 'SERVICE' | 'MAINTENANCE' | 'RENTAL' | 'RENTAL_MAINTENANCE' | 'PRICE_REGISTRATION' | 'OTHER';

export interface ContactPerson {
  id: string;
  name: string;
  role?: string; // Ex: Gestor, Fiscal, Administrativo
  email?: string;
  phone?: string;
}

export interface Contract {
  id: string;
  contractNumber?: string; // Número do contrato (ex: 276/2025)
  tenderId: string;
  title: string;
  organ: string;
  description: string;
  type: ContractType; // Campo Adicionado
  category?: string; // PUBLIC ou PRIVATE
  contractType?: 'STANDARD' | 'SOLAR'; // Classificação do contrato (NOVO)
  startDate: string;
  endDate: string;
  signatureDate?: string; // Data de Assinatura
  // responsible?: string;   // REMOVIDO: Substituído por lista de contatos
  contacts: ContactPerson[]; // NOVO: Lista de contatos
  value: number;
  monthlyValue?: number;
  active: boolean;
  renewalHistory?: RenewalRecord[];
  notes?: string;
  companyId?: string; // Vinculo com a empresa (NOVO)
  file?: AnalyzedFile | null; // Arquivo do Contrato
  fileUrl?: string; // URL/chave S3 do arquivo (vindo do backend)
  // Anexos complementares (múltiplos) - ex: Atestados, ARTs, Planilhas
  attachments?: DocumentAttachment[];
  // Campos específicos para Contratos Solares (NOVO)
  totalMonths?: number; // Total de meses/parcelas do contrato solar (ex: 48)
  billedInstallments?: number; // Quantas parcelas já foram faturadas
  firstBillingDate?: string; // Data do primeiro faturamento (YYYY-MM-DD)
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'model';
  text: string;
  timestamp: Date;
  isError?: boolean;
}

export interface AnalysisSession {
  id: string;
  title?: string; // Título personalizado da sessão
  tenderId?: string;
  file: AnalyzedFile;
  messages: ChatMessage[];
  lastModified: number;
  pdfMissing?: boolean;
}

export type TemplateCategory = 'PROPOSAL' | 'DECLARATION' | 'CERTIFICATE' | 'DATASHEET' | 'OTHER';

export interface DocumentTemplate {
  id: string;
  name: string;
  category: TemplateCategory;
  uploadDate: string;
  size: number;
  type: string;
  content: string;
}

// Interface para Logs de Auditoria
export interface ActivityLog {
  id: string;
  action: string;
  details: string;
  user: string;
  timestamp: string;
  type: 'INFO' | 'WARNING' | 'SUCCESS' | 'ERROR';
}

// Interface para Credenciais de Portais
export interface PortalCredential {
  id: string;
  name: string;
  url: string;
  login: string;
  password?: string;
  authType?: 'USER_PASSWORD' | 'CERTIFICATE' | 'USER_PASSWORD_CERTIFICATE';
  certificateAlias?: string;
  certificateExpiresAt?: string;
  notes?: string;
  updatedAt: string;
  renewalDate?: string; // Data de validade/renovação do cadastro ou senha
  companyId?: string; // Vínculo com a empresa (opcional)
}

// --- NOVOS TIPOS PARA USUÁRIOS ---

export type UserRole = 'ADMIN' | 'MANAGER' | 'OPERATOR' | 'VIEWER';

export interface SystemUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  active: boolean;
  lastLogin?: string;
  avatar?: string; // URL ou iniciais
  department?: string;
  menuKeys?: string[];
  permissionsConfigured?: boolean;
}

// --- NOVO TIPO PARA GUIA DE DOCUMENTOS ---

export type DocType =
  | 'FEDERAL'
  | 'ESTADUAL'
  | 'MUNICIPAL'
  | 'TRABALHISTA'
  | 'FGTS'
  | 'FALENCIA'
  | 'BALANCO'
  | 'ATESTADO'
  | 'OUTROS';

export interface OfficialDocument {
  id: string;
  title: string;
  type: DocType;
  issueDate?: string; // Data de Emissão
  expirationDate?: string; // Data de Validade
  file: AnalyzedFile | null; // O arquivo em si (Legacy/Frontend)
  fileUrl?: string; // URL/Key do S3

  // Novos campos para suporte a CREA/ART e anexos múltiplos
  hasArt?: boolean;
  hasCrea?: boolean;
  attachments?: DocumentAttachment[];
  items?: CertificateItem[];

  issuer: string; // Órgão emissor (ex: Receita Federal)
  category?: string; // Categoria
  status: 'VALID' | 'WARNING' | 'EXPIRED' | 'NO_EXPIRATION';
  companyId?: string; // Vinculo com a empresa (NOVO)

  // Campos específicos para Atestados de Capacidade Técnica
  clientName?: string; // Razão Social do Cliente/Órgão (PRINCIPAL)
  clientCNPJ?: string; // CNPJ do cliente
  reference?: string; // Contrato/NF/Empenho vinculado (SECUNDÁRIO)
  workDescription?: string; // Descrição da obra/serviço executado
  executionPeriodStart?: string; // Início da execução (YYYY-MM-DD)
  executionPeriodEnd?: string; // Fim da execução (YYYY-MM-DD)
  workLocation?: string; // Local/Endereço da obra
  signatoryName?: string; // Nome do signatário (cliente)
  signatoryRole?: string; // Cargo/Função do signatário
  signatoryCPF?: string; // CPF do signatário

  updatedAt: string;
}

// --- NOVO TIPO PARA PERFIL DE EMPRESA ---
export interface CompanyProfile {
  id: string;
  isDefault: boolean;
  corporateName: string; // Razão Social
  tradeName: string; // Nome Fantasia
  cnpj: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  email: string;
  phone: string;
  website?: string; // Novo
  representativeName: string; // Representante Legal
  representativeRole: string; // Cargo do Representante (Sócio, Procurador)
  representativeCpf: string;
  logo?: string; // Base64 da imagem do logo para papel timbrado
  primaryColor?: string; // Cor principal do timbrado (Hex)
  secondaryColor?: string; // Cor secundária do timbrado (Hex)
}

// --- GESTÃO DE EMPENHOS E ORDENS DE SERVIÇO ---
export type OrderType = 'PRODUCT' | 'SERVICE';
// Inclui status históricos e novos do backend (ex: OPEN, OVERDUE, DELIVERED)
export type OrderStatus =
  | 'PENDING'
  | 'APPROVED'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'CANCELLED'
  | 'OPEN'
  | 'OVERDUE'
  | 'DELIVERED';
export type OrderDocumentType = 'EMPENHO' | 'ORDEM_SERVICO' | 'AUTORIZACAO' | 'OUTRO';

export interface PurchaseOrder {
  id: string;
  number: string; // Número do Empenho/OS
  title: string; // Objeto
  type: OrderType; // Produto ou Serviço
  documentType?: OrderDocumentType; // Tipo do documento
  supplier: string; // Órgão Emissor (Cliente)
  tenderId?: string; // Vínculo com a Licitação
  requestDate: string; // Data de Emissão
  deliveryDate?: string; // Prazo de Entrega
  value: number;
  status: OrderStatus;
  description?: string;
  file?: AnalyzedFile | null; // PDF do Empenho
  fileUrl?: string; // URL do S3
  companyId?: string; // Minha empresa executora
}

// --- GESTÃO DE OFÍCIOS E COMUNICAÇÕES ---
export type CommunicationType =
  | 'NOTIFICATION_IN'
  | 'EXTENSION_REQUEST'
  | 'OFFICIAL_LETTER_OUT'
  | 'DEFENSE'
  | 'OTHER';
export type CommunicationStatus =
  | 'DRAFT'
  | 'SENT'
  | 'RECEIVED'
  | 'ACCEPTED'
  | 'REJECTED'
  | 'UNDER_REVIEW';

export interface OfficialCommunication {
  id: string;
  tenderId: string; // Vínculo obrigatório com processo ganho
  type: CommunicationType;
  subject: string;
  date: string; // Data do documento
  deadlineResponse?: string; // Se houver prazo para resposta
  status: CommunicationStatus;
  description?: string;
  file?: AnalyzedFile | null;
  companyId?: string; // Empresa vinculada
  createdAt: string;
}

// --- JURÍDICO ---
export type LegalMatterCategory = 'NOTIFICATIONS' | 'COLLECTION' | 'ADMINISTRATIVE';
export type LegalAdministrativeSubtype = 'APPEAL' | 'COUNTERARGUMENTS' | 'CHALLENGE' | 'WRIT_OF_MANDAMUS';
export type LegalMatterStatus = 'OPEN' | 'IN_PROGRESS' | 'AWAITING' | 'COMPLETED' | 'CANCELLED';
export type LegalMatterPriority = 'LOW' | 'NORMAL' | 'HIGH' | 'URGENT';

export interface LegalMatter {
  id: string;
  category: LegalMatterCategory;
  subtype?: LegalAdministrativeSubtype | null;
  number?: string | null;
  subject: string;
  authority?: string | null;
  opposingParty?: string | null;
  status: LegalMatterStatus;
  priority: LegalMatterPriority;
  openedAt: string;
  deadline?: string | null;
  responsible?: string | null;
  amount?: number | null;
  description?: string | null;
  attachmentName?: string | null;
  attachmentUrl?: string | null;
  tenderId?: string | null;
  tender?: Pick<Tender, 'id' | 'title' | 'organ'> | null;
  createdAt: string;
  updatedAt: string;
}

// --- CADASTRO DE PRODUTOS ---
export interface Product {
  id: string;
  name: string;
  brand: string;
  model: string;
  description: string; // Especificação técnica resumida
  price?: number; // Preço de custo ou referência
  datasheet: AnalyzedFile | null; // Arquivo PDF anexado
  category?: string; // Novo: Categoria (ex: Relógio de Ponto)
  createdAt: string;
}

// --- CONFIGURAÇÕES GERAIS E IA ---
export type AIProvider = 'GEMINI' | 'OPENAI';

export interface NotificationPreferences {
  statusChange: boolean; // Alterações de prioridade
  deadlineWarning: boolean; // Prazos vencendo
  newCommunication: boolean; // Novos ofícios
  contractRenewal: boolean; // Contratos vencendo
}

// Nova configuração para EmailJS
export interface EmailJsConfig {
  serviceId: string;
  templateId: string;
  publicKey: string;
}

// Configuração SMTP para envio direto
export interface SmtpConfig {
  host: string;
  port: number;
  secure: boolean; // true para porta 465, false para 587
  user: string;
  password: string;
  fromName?: string;
  fromEmail?: string;
}

export interface AppSettings {
  id?: string;
  aiProvider: AIProvider;
  openaiApiKey?: string;
  geminiApiKey?: string;

  // Configuração SMTP
  smtpHost?: string;
  smtpPort?: number;
  smtpSecure?: boolean;
  smtpUser?: string;
  smtpPassword?: string;
  smtpFromName?: string;
  smtpFromEmail?: string;

  // Destinatários de Notificações
  emailRecipients?: string;

  // Preferências de Notificação
  notifyStatusChange?: boolean;
  notifyDeadlineWarning?: boolean;
  notifyNewCommunication?: boolean;
  notifyContractRenewal?: boolean;

  // Deprecated: EmailJS (mantido para compatibilidade)
  notificationPreferences?: NotificationPreferences;
  emailJsConfig?: EmailJsConfig;

  createdAt?: Date;
  updatedAt?: Date;
}

export interface DocumentAttachment {
  id: string;
  name: string;
  url: string;
  type?: string;
  size?: number;
  uploadedAt: string;
}

export interface CertificateItem {
  id: string;
  description: string;
  quantity: number;
  unit?: string;
}
